import { Button, Col, Divider, Input, Modal, Row, Select, Space, Spin, Switch, Radio, Tag, Alert, Typography, Table } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { PlusOutlined, UserOutlined, TeamOutlined, SolutionOutlined } from "@ant-design/icons";
import { getParametre, saveDecaissements } from "../../../../hooks/useHooks";
import axiosInstance from "../../../../variables/api/axiosInstance";
import { toast } from "react-toastify";
import { PARAMETERS } from "../../../../variables/constants";
import AmountInputNumber from "../../../../components/inputs/AmountInputNumber";
import { formatAmount } from "../../../../variables/numbersFunctions";
import { libelleGarantie } from "../../../../variables/stringFunctions";

const { TextArea } = Input;

// Clé d'une garantie pour la ventilation (codeId si présent, sinon nom).
const gkeyVent = (g) => g?.garantieCodeId || g?.garantieNom || "?";

// Clé de repli quand le dossier n'a pas de référentiel de garanties : montant unique par nature.
const VENT_NONE = "__SANS_GARANTIE__";
// Libellés d'affichage des natures de règlement.
const NATURE_LABELS = {
    "SINISTRE EN PRINCIPAL": "Sinistre en principal",
    "ACCESSOIRES": "Frais",
    "HONORAIRES": "Honoraires",
    "PROVISION": "Provision",
};
const labelNature = (v) => NATURE_LABELS[v] || v;

// Options de motif dé-dupliquées par LIBELLÉ affiché (insensible casse/espaces) : évite toute
// répétition visuelle dans la liste déroulante, quelle que soit la réponse serveur.
const dedupMotifOptions = (motifs) => {
    const seen = new Set();
    const out = [];
    (motifs || []).forEach((m) => {
        const label = m?.libelleParametre || m?.valeur || "";
        const value = m?.valeur || m?.libelleParametre || "";
        const key = String(label).trim().toLowerCase();
        if (!key || seen.has(key)) return;
        seen.add(key);
        out.push({ label, value });
    });
    return out;
};

const ALL_BENEFICIARY_CATEGORIES = ["victimes", "experts", "investigateurs", "accessoires", "conseils"];

// Natures sur lesquelles le TAUX DE RESPONSABILITÉ du dossier est appliqué au pré-remplissage
// « prorata SAP » : SINISTRE EN PRINCIPAL et FRAIS (ACCESSOIRES). JAMAIS Honoraires ni Provision.
const NATURES_PONDEREES_RESPONSABILITE = ["SINISTRE EN PRINCIPAL", "ACCESSOIRES"];

const REGLEMENT_NATURE_CATEGORY_MAP = {
    "SINISTRE EN PRINCIPAL": ["victimes"],
    // Les frais accessoires peuvent aussi être réglés à un EXPERT, un INVESTIGATEUR (leurs
    // frais/déplacements, à valeur faciale) ou au CONSEIL JURIDIQUE du dossier (frais décomposés).
    ACCESSOIRES: ["victimes", "accessoires", "experts", "investigateurs", "conseils"],
    // Honoraires : experts, investigateurs ET conseil juridique (honoraires + TVA + retenue).
    HONORAIRES: ["experts", "investigateurs", "conseils"],
    PROVISION: ["victimes"],
};

// Postes de FRAIS d'un conseil juridique → nom du champ montant dans DecaissementDTO
// (endpoint /api/decaissement/all-conseils-juridiques). Chaque poste = valeur faciale.
const POSTES_FRAIS_CONSEIL = [
    { label: "Frais de gestion", value: "fraisGestion" },
    { label: "Frais de PV de constat", value: "fraisPvConstat" },
    { label: "Frais de vignette", value: "fraisVignette" },
    { label: "Frais de déplacement", value: "fraisDeplacement" },
];
// Valeurs par défaut (mêmes que l'écran dédié conseils juridiques) : retenue 5 %, TVA 18 %,
// appliquées UNIQUEMENT aux honoraires (frais à valeur faciale).
const CONSEIL_RETENUE_DEFAUT = 5;
const CONSEIL_TVA_DEFAUT = 18;

const normalizeOptionValue = (value) => {
    if (value === null || value === undefined) {
        return null;
    }
    const normalized = `${value}`.trim();
    return normalized.length ? normalized : null;
};

const dedupeOptionList = (options = []) => {
    const seen = new Set();
    return options.filter((option) => {
        const val = option?.value;
        if (!val) {
            return false;
        }
        if (seen.has(val)) {
            return false;
        }
        seen.add(val);
        return true;
    });
};

const buildAccessoireLabel = (item = {}) => {
    const candidates = [
        item?.libelle,
        item?.libelleParametre,
        item?.nom && item?.prenom ? `${item.nom} ${item.prenom}` : undefined,
        item?.nom,
        item?.intitule,
        item?.designation,
        item?.raisonSociale,
        item?.label,
        item?.personne?.raisonSociale,
        item?.personne?.nom,
    ];
    const label = candidates.find((candidate) => typeof candidate === "string" && candidate.trim().length);
    if (label) {
        return label.trim();
    }
    const fallbackValue = normalizeOptionValue(
        item?.codeId || item?.id || item?.value || item?.libelle || item?.libelleParametre
    );
    return fallbackValue ? `Accessoire ${fallbackValue}` : "Accessoire";
};

const buildNatureActeOption = (item = {}) => {
    const candidates = [
        item?.libelleParametre,
        item?.valeur,
        item?.label,
        item?.libelle,
        item?.designation,
        item?.nom,
    ];
    const label = candidates.find((candidate) => typeof candidate === "string" && candidate.trim().length);
    if (!label) {
        return null;
    }
    const normalized = label.trim();
    return {
        label: normalized,
        value: normalized,
    };
};

const formatRepresentantName = (representant) => {
    if (!representant) {
        return null;
    }
    const parts = [representant?.nom, representant?.prenom].filter((part) => typeof part === "string" && part.trim().length);
    return parts.length ? parts.join(" ").trim() : null;
};

const ReglementModal = ({ sinistre, modalControl, sap, sapMin, handleClotureDossier }) => {
    const victimes = sinistre?.victimes || [];
    const experts = sinistre?.experts || [];
    const investigateurs = sinistre?.investigateurs || [];
    // Conseil juridique associé au dossier (personne morale) — réglable directement ici.
    const conseilJuridique = sinistre?.conseilJuridique || null;
    // Sous-flux conseil juridique. Honoraires ET plusieurs postes de frais peuvent être saisis
    // ENSEMBLE → un seul règlement groupé (backend : groupeReglementCodeId, 1 décaissement/poste).
    const [montantHonorairesConseil, setMontantHonorairesConseil] = useState(0);
    // Montant par poste de frais (nom du champ = champ DTO). Plusieurs postes possibles.
    const [fraisConseil, setFraisConseil] = useState({ fraisGestion: 0, fraisPvConstat: 0, fraisVignette: 0, fraisDeplacement: 0 });
    const [tauxTvaConseil, setTauxTvaConseil] = useState(CONSEIL_TVA_DEFAUT);
    // Ventilation par garantie du règlement conseil (poids par garantie → répartie au prorata
    // sur CHAQUE décaissement créé, dont la somme doit égaler son montantGlobal). Facultative.
    const [ventConseil, setVentConseil] = useState({});
    const [selectedBeneficiaireId, setSelectedBeneficiaireId] = useState("");
    const [sousEvaluationsSelected, setSousEvaluationsSelected] = useState([]);
    const [natureReglement, setNatureReglement] = useState([]);
    // Ventilation par nature × garantie : { nature: { cléGarantie: montant } }.
    // Le montant de chaque nature ET le total DÉCOULENT de cette ventilation (piloté par la ventilation).
    const [ventNature, setVentNature] = useState({});
    const [ventCible, setVentCible] = useState({}); // nature -> montant à répartir (bouton prorata SAP)
    // Règlement groupé : un seul ordre de dépense / fiche / quittance / chèque
    // pour toutes les natures saisies (activé par défaut).
    const [regrouper, setRegrouper] = useState(true);
    const [beneficiaire, setBeneficiaire] = useState(null);
    const [selectedBeneficiaryOption, setSelectedBeneficiaryOption] = useState(null);
    const [honoraire, setHonoraire] = useState(0);
    const [decaissementLoading, setDecaissementLoading] = useState(false)
    const [dossierCloture, setDossierCloture] = useState(false);
    const [motifsLoading, setMotifsLoading] = useState(false);
    const [motifs, setMotifs] = useState([]);
    const [name, setName] = useState('');
    const [motifReglement, setMotifReglement] = useState();
    const [textValue, setTextValue] = useState('');
    const [natureActeOptions, setNatureActeOptions] = useState([]);
    const [natureActeLoading, setNatureActeLoading] = useState(false);
    const [natureActe, setNatureActe] = useState();
    // Reste disponible = SAP − paiements en attente (validation ou chèque) : grise le
    // bouton « Valider » dès que la somme saisie dépasse (garde serveur en plus).
    const [resteDisponible, setResteDisponible] = useState(null);
    // Ventilation par garantie (facultative) : imputer le montant du règlement aux garanties du
    // dossier pour suivre le SAP par garantie. Référentiel = /api/sinistre/sap-garanties.
    const [sapGarantiesVent, setSapGarantiesVent] = useState([]);

    // Dérivés (pilotés par la ventilation) : montant par nature = Σ de sa ventilation par garantie ;
    // le montant total du règlement = Σ des natures.
    const montantParNature = useMemo(() => {
        const out = {};
        (natureReglement || []).forEach((n) => {
            const row = ventNature[n] || {};
            out[n] = Object.values(row).reduce((a, v) => a + (Number(v) || 0), 0);
        });
        return out;
    }, [natureReglement, ventNature]);
    const montantNature = useMemo(
        () => (natureReglement || []).map((n) => ({ nature: n, montant: montantParNature[n] || 0 })),
        [natureReglement, montantParNature]
    );
    const montantReglement = useMemo(
        () => (natureReglement || []).reduce((a, n) => a + (Number(montantParNature[n]) || 0), 0),
        [natureReglement, montantParNature]
    );

    // Écrit une cellule de la matrice (nature, garantie).
    const setVentCell = (nature, gkey, val) => {
        setVentNature((prev) => ({
            ...prev,
            [nature]: { ...(prev[nature] || {}), [gkey]: Number(val) || 0 },
        }));
    };

    useEffect(() => {
        if (!modalControl?.open || !sinistre?.codeId) return;
        axiosInstance.get(`/api/decaissement/reste-sap/${sinistre.codeId}`)
            .then((res) => setResteDisponible(Number(res.data?.resteDisponible)))
            .catch(() => setResteDisponible(null)); // indisponible → pas de grisage, garde serveur
        axiosInstance.get(`/api/sinistre/sap-garanties/${sinistre.codeId}`)
            .then((res) => setSapGarantiesVent(res.data || []))
            .catch(() => setSapGarantiesVent([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalControl?.open, sinistre?.codeId]);

    // Taux de responsabilité du dossier (défaut 100). Appliqué UNIQUEMENT au pré-remplissage prorata
    // SAP des natures Principal/Frais (pas Honoraires ni Provision).
    const tauxResponsabilite = Math.max(0, Math.min(100, Number(sinistre?.tauxResponsabilite ?? 100) || 100));

    // Répartit un montant cible au prorata du SAP de chaque garantie, DANS une nature donnée.
    // Pour Principal/Frais, le montant cible est d'abord pondéré par le taux de responsabilité.
    const repartirNatureProrataSap = (nature, cible) => {
        const brut = Number(cible) || 0;
        const total = (tauxResponsabilite < 100 && NATURES_PONDEREES_RESPONSABILITE.includes(nature))
            ? Math.round(brut * tauxResponsabilite / 100)
            : brut;
        const totalSap = sapGarantiesVent.reduce((a, g) => a + (Number(g.sap) || 0), 0);
        const next = {};
        if (totalSap > 0 && total > 0) {
            sapGarantiesVent.forEach((g) => { next[gkeyVent(g)] = Math.round((Number(g.sap) || 0) / totalSap * total); });
            const s = Object.values(next).reduce((a, v) => a + v, 0);
            const diff = Math.round(total) - s;
            if (diff !== 0 && sapGarantiesVent.length) {
                const gmax = sapGarantiesVent.reduce((m, g) => ((next[gkeyVent(g)] || 0) > (next[gkeyVent(m)] || 0) ? g : m), sapGarantiesVent[0]);
                next[gkeyVent(gmax)] = (next[gkeyVent(gmax)] || 0) + diff;
            }
        }
        setVentNature((prev) => ({ ...prev, [nature]: next }));
    };
    // Lignes de ventilation (garantieCodeId/garantieNom/montant) pour une matrice { gkey: montant }.
    const buildVentLines = (row) => sapGarantiesVent
        .map((g) => ({ garantieCodeId: g.garantieCodeId || null, garantieNom: g.garantieNom || null, montant: Number((row || {})[gkeyVent(g)]) || 0 }))
        .filter((l) => l.montant > 0);

    // Après création des décaissements : impute à CHAQUE ligne créée SA ventilation exacte
    // (matrice nature × garantie). Plus de répartition au prorata — chaque nature porte sa propre
    // ventilation. Cas groupé : un seul décaissement → matrice fusionnée (Σ des natures par garantie).
    const ventilerDecaissementsCrees = async (created, grouped) => {
        if (!created?.length || !sapGarantiesVent.length) return;
        if (grouped) {
            const merged = {};
            (natureReglement || []).forEach((n) => {
                const row = ventNature[n] || {};
                Object.keys(row).forEach((k) => { merged[k] = (Number(merged[k]) || 0) + (Number(row[k]) || 0); });
            });
            const lines = buildVentLines(merged);
            if (lines.length) await axiosInstance.put(`/api/decaissement/${created[0].codeId}/ventilation`, lines);
            return;
        }
        for (let i = 0; i < created.length; i++) {
            const d = created[i];
            const nature = d?.nature || montantNature[i]?.nature; // écho backend, sinon ordre d'envoi
            const lines = buildVentLines(ventNature[nature] || {});
            if (lines.length) await axiosInstance.put(`/api/decaissement/${d.codeId}/ventilation`, lines);
        }
    };

    // CONSEIL JURIDIQUE : la ventilation par garantie est saisie en POIDS (ventConseil), puis
    // répartie AU PRORATA sur CHAQUE décaissement créé (honoraires, chaque poste de frais), de
    // sorte que la somme des lignes égale le montantGlobal du décaissement (contrainte serveur).
    // Facultative : sans poids saisis, on ne ventile pas.
    const ventilerConseilCrees = async (created) => {
        if (!created?.length || !sapGarantiesVent.length) return;
        const poids = sapGarantiesVent.map((g) => ({ g, w: Number(ventConseil[gkeyVent(g)]) || 0 }));
        const totalW = poids.reduce((a, p) => a + p.w, 0);
        if (!(totalW > 0)) return;
        for (const d of created) {
            const M = Math.round(Number(d?.montantGlobal) || 0);
            if (!(M > 0)) continue;
            const lignes = poids.map((p) => ({
                garantieCodeId: p.g.garantieCodeId || null,
                garantieNom: p.g.garantieNom || null,
                montant: Math.round(M * p.w / totalW),
            }));
            const s = lignes.reduce((a, l) => a + l.montant, 0);
            const diff = M - s;
            if (diff !== 0 && lignes.length) {
                let idxMax = 0;
                lignes.forEach((l, i) => { if (l.montant > lignes[idxMax].montant) idxMax = i; });
                lignes[idxMax].montant += diff;
            }
            const clean = lignes.filter((l) => l.montant > 0);
            if (clean.length) await axiosInstance.put(`/api/decaissement/${d.codeId}/ventilation`, clean);
        }
    };
    // Formule de conclusion : déplacée à l'édition de la quittance (cf. AssignCheque), plus à l'init.

    const accessoiresBeneficiaires = useMemo(() => {
        const candidateKeys = [
            "accessoiresBeneficiaires",
            "beneficiairesAccessoires",
            "fraisAccessoiresBeneficiaires",
            "fraisAccessoires",
            "accessoires",
        ];
        const aggregated = [];
        candidateKeys.forEach((key) => {
            const items = sinistre?.[key];
            if (Array.isArray(items)) {
                aggregated.push(...items.filter(Boolean));
            }
        });
        if (!aggregated.length && Array.isArray(sinistre?.accessoire)) {
            aggregated.push(...sinistre.accessoire.filter(Boolean));
        }
        return aggregated.filter(Boolean);
    }, [sinistre]);

    const victimesOptions = useMemo(() => {
        const options = (victimes || [])
            .filter((victime) => normalizeOptionValue(victime?.codeId) && victime?.personne)
            .map((victime) => {
                const value = normalizeOptionValue(victime.codeId);
                if (!value) {
                    return null;
                }
                const personne = victime?.personne || {};
                const labelCandidates = [
                    personne?.nom && personne?.prenom ? `${personne.nom} ${personne.prenom}` : undefined,
                    personne?.raisonSociale,
                    personne?.nom,
                    personne?.prenom,
                ];
                const label =
                    labelCandidates.find((candidate) => typeof candidate === "string" && candidate.trim().length) || "Victime";

                return {
                    label: label.trim(),
                    value,
                    beneficiary: { type: "victimes", item: victime },
                };
            })
            .filter(Boolean);
        return dedupeOptionList(options);
    }, [victimes]);

    const expertsOptions = useMemo(() => {
        const options = (experts || [])
            .filter((expert) => normalizeOptionValue(expert?.codeId))
            .map((expert) => {
                const value = normalizeOptionValue(expert.codeId);
                if (!value) {
                    return null;
                }
                const labelCandidates = [
                    expert?.nom && expert?.prenom ? `${expert.nom} ${expert.prenom}` : undefined,
                    expert?.raisonSociale,
                    expert?.nom,
                    expert?.prenom,
                ];
                const label =
                    labelCandidates.find((candidate) => typeof candidate === "string" && candidate.trim().length) || `Expert ${value}`;

                return {
                    label: label.trim(),
                    value,
                    beneficiary: { type: "experts", item: expert },
                };
            })
            .filter(Boolean);
        return dedupeOptionList(options);
    }, [experts]);

    // Investigateurs (Personnes profession INVESTIGATEUR affectées au dossier) — sélectionnables
    // comme des experts (honoraires / frais d'investigation).
    const investigateursOptions = useMemo(() => {
        const options = (investigateurs || [])
            .filter((inv) => normalizeOptionValue(inv?.codeId))
            .map((inv) => {
                const value = normalizeOptionValue(inv.codeId);
                if (!value) {
                    return null;
                }
                const labelCandidates = [
                    inv?.nom && inv?.prenom ? `${inv.nom} ${inv.prenom}` : undefined,
                    inv?.raisonSociale,
                    inv?.nom,
                    inv?.prenom,
                ];
                const label =
                    labelCandidates.find((candidate) => typeof candidate === "string" && candidate.trim().length) || `Investigateur ${value}`;
                return {
                    label: label.trim(),
                    value,
                    beneficiary: { type: "investigateurs", item: inv },
                };
            })
            .filter(Boolean);
        return dedupeOptionList(options);
    }, [investigateurs]);

    const accessoiresOptions = useMemo(() => {
        const options = (accessoiresBeneficiaires || [])
            .map((accessoire) => {
                const possibleIds = [
                    accessoire?.codeId,
                    accessoire?.id,
                    accessoire?.value,
                    accessoire?.libelle,
                    accessoire?.libelleParametre,
                ];
                const rawValue = possibleIds.map((id) => normalizeOptionValue(id)).find((val) => val);
                if (!rawValue) {
                    return null;
                }
                return {
                    label: buildAccessoireLabel(accessoire),
                    value: rawValue,
                    beneficiary: { type: "accessoires", item: accessoire },
                };
            })
            .filter(Boolean);
        return dedupeOptionList(options);
    }, [accessoiresBeneficiaires]);

    // Option unique du conseil juridique associé au dossier.
    const conseilsOptions = useMemo(() => {
        const value = normalizeOptionValue(conseilJuridique?.conseilJuridiqueId);
        if (!value) {
            return [];
        }
        return [{
            label: conseilJuridique?.nom || `Conseil ${value}`,
            value,
            beneficiary: { type: "conseils", item: conseilJuridique },
        }];
    }, [conseilJuridique]);

    const beneficiaryOptionLookup = useMemo(() => {
        const map = new Map();
        [...victimesOptions, ...expertsOptions, ...investigateursOptions, ...accessoiresOptions, ...conseilsOptions].forEach((option) => {
            if (option?.value) {
                map.set(option.value, option?.beneficiary?.item);
            }
        });
        return map;
    }, [victimesOptions, expertsOptions, investigateursOptions, accessoiresOptions, conseilsOptions]);

    const allowedBeneficiaryCategories = useMemo(() => {
        if (!natureReglement?.length) {
            return ALL_BENEFICIARY_CATEGORIES;
        }
        const intersection = natureReglement.reduce((acc, nature) => {
            const allowedForNature = REGLEMENT_NATURE_CATEGORY_MAP[nature] || ALL_BENEFICIARY_CATEGORIES;
            if (!acc) {
                return [...allowedForNature];
            }
            return acc.filter((category) => allowedForNature.includes(category));
        }, null);
        return Array.isArray(intersection) ? intersection : [];
    }, [natureReglement]);

    const beneficiaryOptionGroups = useMemo(() => {
        const groups = [];
        if (allowedBeneficiaryCategories.includes("victimes") && victimesOptions.length) {
            groups.push({
                label: <span>Victime(s)</span>,
                title: "Victime(s)",
                options: victimesOptions,
            });
        }
        if (allowedBeneficiaryCategories.includes("experts") && expertsOptions.length) {
            groups.push({
                label: <span>Expert(s)</span>,
                title: "Expert(s)",
                options: expertsOptions,
            });
        }
        if (allowedBeneficiaryCategories.includes("investigateurs") && investigateursOptions.length) {
            groups.push({
                label: <span>Investigateur(s)</span>,
                title: "Investigateur(s)",
                options: investigateursOptions,
            });
        }
        if (allowedBeneficiaryCategories.includes("accessoires") && accessoiresOptions.length) {
            groups.push({
                label: <span>Accessoire(s)</span>,
                title: "Accessoire(s)",
                options: accessoiresOptions,
            });
        }
        if (allowedBeneficiaryCategories.includes("conseils") && conseilsOptions.length) {
            groups.push({
                label: <span>Conseil juridique</span>,
                title: "Conseil juridique",
                options: conseilsOptions,
            });
        }
        return groups;
    }, [allowedBeneficiaryCategories, victimesOptions, expertsOptions, investigateursOptions, accessoiresOptions, conseilsOptions]);

    // Conseil juridique sélectionné comme bénéficiaire → sous-flux dédié (endpoint conseils).
    const isConseilSelected = selectedBeneficiaryOption?.beneficiary?.type === "conseils";

    useEffect(() => {
        if (!selectedBeneficiaireId) {
            return;
        }
        const availableValues = beneficiaryOptionGroups.reduce((acc, group) => {
            if (Array.isArray(group?.options)) {
                group.options.forEach((option) => {
                    if (option?.value) {
                        acc.push(option.value);
                    }
                });
            }
            return acc;
        }, []);
        if (!availableValues.includes(selectedBeneficiaireId)) {
            setSelectedBeneficiaireId("");
            setBeneficiaire(null);
            setSelectedBeneficiaryOption(null);
        }
    }, [beneficiaryOptionGroups, selectedBeneficiaireId]);

    useEffect(() => {
        if (!modalControl?.open) {
            setSelectedBeneficiaireId("");
            setBeneficiaire(null);
            setSelectedBeneficiaryOption(null);
            setNatureActe(undefined);
            return;
        }

        setSelectedBeneficiaireId("");
        setBeneficiaire(null);
        setSelectedBeneficiaryOption(null);
        setSousEvaluationsSelected([]);
        setNatureReglement([]);
        setVentNature({});
        setVentCible({});
        setHonoraire(0);
        setMotifReglement(undefined);
        setTextValue('');
        setName('');
        setNatureActe(undefined);
    }, [modalControl?.open]);

    const beneficiarySelectDisabled = beneficiaryOptionGroups.length === 0;

    const inputRef = useRef(null);

    useEffect(() => {
        if (parseFloat(montantReglement) === parseFloat(sap) - parseFloat(sapMin)) {
            setDossierCloture(true);
        } else {
            setDossierCloture(false);
        }
    }, [montantReglement, sap, sapMin]);

    useEffect(() => {
        const fetchMotifs = async () => {
            setMotifsLoading(true);
            const response = await getParametre(PARAMETERS.motifReglement);
            // console.log("motifs: ", response.data);
            setMotifs(response.data);
            setMotifsLoading(false);
        };
        fetchMotifs()
    }, []);


    useEffect(() => {
        const fetchNatureActes = async () => {
            try {
                setNatureActeLoading(true);
                const response = await getParametre(PARAMETERS.natureActe);
                const options = dedupeOptionList(
                    (response?.data || [])
                        .map((item) => buildNatureActeOption(item))
                        .filter(Boolean)
                );
                setNatureActeOptions(options);
            } catch (error) {
                setNatureActeOptions([]);
            } finally {
                setNatureActeLoading(false);
            }
        };

        fetchNatureActes();
    }, []);

    // Called when a motif is selected
    const onMotifSelect = (value) => {
        handleFindMotif(value); // This should eventually call setTextValue with content
        // La valeur de l'option = valeur || libelleParametre (voir mapping du Select) ; on
        // réplique le libellé lisible dans la zone détaillée (source de vérité à l'enregistrement).
        const selectedOption = motifs.find(opt => (opt.valeur || opt.libelleParametre) === value);
        setTextValue(selectedOption?.libelleParametre || selectedOption?.valeur || value || '');
    };


    const handleFindMotif = (e) => {
        setMotifReglement(e)
    }


    const handleBeneficiaireChange = (value, option) => {
        const normalizedValue = normalizeOptionValue(value) || "";
        setSelectedBeneficiaireId(normalizedValue);
        // Réinitialise le sous-flux conseil à chaque changement de bénéficiaire.
        setMontantHonorairesConseil(0);
        setFraisConseil({ fraisGestion: 0, fraisPvConstat: 0, fraisVignette: 0, fraisDeplacement: 0 });
        setTauxTvaConseil(CONSEIL_TVA_DEFAUT);
        setVentConseil({});

        if (!normalizedValue) {
            setBeneficiaire(null);
            setSelectedBeneficiaryOption(null);
            return;
        }

        const optionSource = Array.isArray(option) ? option[0] : option;
        setSelectedBeneficiaryOption(optionSource || null);
        const beneficiaryFromOption = optionSource?.beneficiary?.item;
        if (beneficiaryFromOption) {
            setBeneficiaire(beneficiaryFromOption);
            return;
        }

        const fallback = beneficiaryOptionLookup.get(normalizedValue);
        setBeneficiaire(fallback || null);
    };

    // Victime sélectionnée comme bénéficiaire (et non expert / accessoire).
    const selectedVictime = useMemo(() => {
        const isVictimeSelection =
            selectedBeneficiaryOption?.beneficiary?.type === "victimes" ||
            (!!beneficiaire?.representant && !!beneficiaire?.personne) ||
            (!!beneficiaire?.personne && (beneficiaire?.blesseSinistre || beneficiaire?.decesSinistre));
        if (!isVictimeSelection) return null;
        return selectedBeneficiaryOption?.beneficiary?.item || beneficiaire || null;
    }, [beneficiaire, selectedBeneficiaryOption]);

    // Candidats « représentant » = TOUS les représentants du vivier (principal = par défaut en
    // tête → pré-sélectionné) + les ayants droit de la victime. On déduplique par nom.
    const representantCandidates = useMemo(() => {
        if (!selectedVictime) return [];
        const list = [];
        // Principal (par défaut = le plus récent) en PREMIER → pré-sélectionné.
        const principal = selectedVictime?.representant;
        if (principal && formatRepresentantName(principal)) {
            list.push({ value: formatRepresentantName(principal), role: "Représentant (par défaut)" });
        }
        // Les autres représentants du vivier (plusieurs possibles).
        (selectedVictime?.representants || []).forEach((r) => {
            const n = formatRepresentantName(r);
            if (n && !list.some((c) => c.value === n)) list.push({ value: n, role: "Représentant" });
        });
        // Ayants droit.
        (selectedVictime?.ayantsDroit || []).forEach((a) => {
            const n = formatRepresentantName(a);
            if (n && !list.some((c) => c.value === n)) list.push({ value: n, role: "Ayant droit" });
        });
        return list;
    }, [selectedVictime]);

    // Choix du destinataire du chèque : la victime ou un représentant.
    const [chequeMode, setChequeMode] = useState("VICTIME");
    const [chequeRepresentant, setChequeRepresentant] = useState(null);

    useEffect(() => {
        const decede = !!selectedVictime?.decesSinistre;
        setChequeMode(decede && representantCandidates.length ? "REPRESENTANT" : "VICTIME");
        setChequeRepresentant(representantCandidates.length ? representantCandidates[0].value : null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBeneficiaireId]);

    // Nom envoyé au backend (null = à l'ordre de la victime).
    const selectedRepresentantName =
        selectedVictime && chequeMode === "REPRESENTANT" ? chequeRepresentant : null;

    // Le représentant choisi est-il un ayant droit ? (préfixe « AD » sur le document)
    const selectedRepresentantIsAyantDroit =
        !!selectedRepresentantName &&
        representantCandidates.find((c) => c.value === selectedRepresentantName)?.role === "Ayant droit";

    const victimeName = formatRepresentantName(selectedVictime?.personne) || "la victime";

    const handleSousEvChange = (value) => {
        setSousEvaluationsSelected(value);
    };

    const addItem = (e) => {
        e.preventDefault();

        if (!name?.trim()) {
            return; // Prevent adding empty or whitespace-only items
        }
        setMotifs((prevOptions) => [...prevOptions, { libelleParametre: name, valeur: name }]);

        setName(""); // Clear the input field

        inputRef.current?.focus(); // Focus the input directly
    };

    const handleNatureActeChange = (value) => {
        setNatureActe(value || undefined);
    };

    const montantDemande = (montantNature || []).reduce((a, mn) => a + (parseFloat(mn?.montant) || 0), 0);
    const depasseSap = resteDisponible != null && montantDemande > resteDisponible + 0.01;

    return (<>
        <Modal
            title={'Règlement de sinistre'}
            open={modalControl.open}
            confirmLoading={decaissementLoading}
            onCancel={() => {
                modalControl.setOpen(false)
                setBeneficiaire(null)
                setSelectedBeneficiaireId("")
                setNatureActe(undefined)
            }}
            okText={depasseSap ? "Montant supérieur au disponible" : "Valider"}
            okButtonProps={{ disabled: depasseSap }}
            cancelText="Annuler"
            width={920}
            zIndex={2000}
            onOk={async () => {
                setDecaissementLoading(true);
                if (!beneficiaire) {
                    toast.error("Veuillez choisir un beneficiaire");
                    setDecaissementLoading(false);
                    return;
                }
                // ── Sous-flux CONSEIL JURIDIQUE ──────────────────────────────────────────
                // Bénéficiaire = conseil du dossier → on N'utilise PAS le chemin générique
                // (/all + ventilation) mais l'endpoint dédié qui porte les règles fiscales
                // (TVA/retenue sur honoraires, frais à valeur faciale, unicité gestion/PV,
                // plafond de déplacement, ventilation annexe). Honoraires ET Frais peuvent être
                // saisis ensemble → un SEUL DTO → règlement GROUPÉ (groupeReglementCodeId côté back).
                if (isConseilSelected) {
                    const veutHonoraires = (natureReglement || []).includes("HONORAIRES");
                    const veutFrais = (natureReglement || []).includes("ACCESSOIRES");
                    if (!veutHonoraires && !veutFrais) {
                        toast.error("Pour un conseil juridique : choisissez Honoraires et/ou Frais.");
                        setDecaissementLoading(false);
                        return;
                    }
                    const mHono = parseFloat(montantHonorairesConseil) || 0;
                    const totalFrais = Object.values(fraisConseil).reduce((a, v) => a + (parseFloat(v) || 0), 0);
                    if (veutHonoraires && !(mHono > 0)) {
                        toast.error("Veuillez renseigner le montant des honoraires.");
                        setDecaissementLoading(false);
                        return;
                    }
                    if (veutFrais && !(totalFrais > 0)) {
                        toast.error("Veuillez renseigner au moins un poste de frais (montant > 0).");
                        setDecaissementLoading(false);
                        return;
                    }
                    const dto = {
                        codeId: sinistre?.codeId,
                        conseilJuridiqueId: conseilJuridique?.conseilJuridiqueId,
                        honoraires: 0, fraisGestion: 0, fraisPvConstat: 0, fraisVignette: 0, fraisDeplacement: 0,
                        retenue: 0, tva: 0,
                    };
                    if (veutHonoraires) {
                        dto.honoraires = mHono;
                        dto.retenue = parseFloat(honoraire) || CONSEIL_RETENUE_DEFAUT;
                        dto.tva = parseFloat(tauxTvaConseil) || 0;
                    }
                    if (veutFrais) {
                        // Un montant par poste de frais (plusieurs postes possibles simultanément).
                        dto.fraisGestion = parseFloat(fraisConseil.fraisGestion) || 0;
                        dto.fraisPvConstat = parseFloat(fraisConseil.fraisPvConstat) || 0;
                        dto.fraisVignette = parseFloat(fraisConseil.fraisVignette) || 0;
                        dto.fraisDeplacement = parseFloat(fraisConseil.fraisDeplacement) || 0;
                    }
                    try {
                        const resp = await axiosInstance.post("/api/decaissement/all-conseils-juridiques", JSON.stringify([dto]), {
                            headers: { "Content-Type": "application/json" },
                        });
                        const created = resp?.data || [];
                        // Ventilation par garantie (facultative) répartie au prorata sur chaque poste créé.
                        if (created.length) {
                            try {
                                await ventilerConseilCrees(created);
                            } catch (ventErr) {
                                toast.warn("Règlement créé, mais la ventilation par garantie a échoué. Vous pourrez la refaire via « Ventiler par garantie ».");
                            }
                        }
                        toast.success("Règlement du conseil juridique effectué !");
                        setDecaissementLoading(false);
                        modalControl.setOpen(false);
                        window.location.reload();
                    } catch (e) {
                        setDecaissementLoading(false);
                        toast.error(e?.response?.data?.message || e?.response?.data || "Une erreur s'est produite !");
                    }
                    return;
                }
                // Le motif enregistré = zone détaillée éditable (textValue) si renseignée,
                // sinon le motif choisi dans la liste. On accepte donc l'un OU l'autre.
                const motifFinal = ((textValue || '').trim()) || (motifReglement || '');
                if (!motifFinal) {
                    toast.error("Veuillez choisir un motif de reglement");
                    setDecaissementLoading(false);
                    return;
                }
                if (!natureReglement?.length) {
                    toast.error("Veuillez choisir le type de reglement");
                    setDecaissementLoading(false);
                    return;
                }
                if (natureReglement.includes("HONORAIRES") && !honoraire) {
                    toast.error("Veuillez choisir le taux de la retenue");
                    setDecaissementLoading(false);
                    return;
                }
                // Montant piloté par la ventilation : chaque nature doit avoir un montant > 0
                // (= somme de sa ventilation par garantie), donc un total global > 0.
                const naturesVides = (natureReglement || []).filter((n) => !((Number(montantParNature[n]) || 0) > 0));
                if (naturesVides.length) {
                    toast.error(`Veuillez ventiler un montant par garantie pour : ${naturesVides.map(labelNature).join(", ")}.`);
                    setDecaissementLoading(false);
                    return;
                }
                if (!(Number(montantReglement) > 0)) {
                    toast.error("Veuillez renseigner au moins un montant de règlement");
                    setDecaissementLoading(false);
                    return;
                }
                let results = [];
                montantNature?.forEach((mn) => {
                    let result = {
                        beneficiaire: beneficiaire.personne ? beneficiaire?.personne : beneficiaire,
                        prejudices: sousEvaluationsSelected,
                        montantGlobal: parseFloat(mn.montant || 0),
                        sinistre: sinistre,
                        motifReglement: motifFinal,
                        // Formule de conclusion désormais saisie à l'édition de la quittance (AssignCheque).
                        formuleDeFin: null,
                        taux: mn?.nature === "HONORAIRES" ? parseFloat(honoraire) : 0,
                        taxes: mn?.nature === "HONORAIRES" ? parseFloat(montantNature?.find((nature) => nature.nature === "HONORAIRES")?.montant || 0) * (parseFloat(honoraire) / 100) : 0,
                        expertise: mn?.nature === "HONORAIRES" ? true : false,
                        nature: mn.nature,
                        natureActe: natureActe,
                        representant: selectedRepresentantName || null,
                        representantAyantDroit: !!selectedRepresentantIsAyantDroit,
                        encaissement: false
                    };
                    results.push(result)
                })

                try {
                    // Regroupement effectif uniquement si plusieurs natures saisies.
                    const grouped = regrouper && results.length > 1;
                    const resp = await saveDecaissements(results, grouped);
                    const created = resp?.data || [];
                    // Ventilation par garantie : chaque nature porte SA propre ventilation (matrice
                    // nature × garantie). Cas groupé : matrice fusionnée sur le décaissement unique.
                    if (created.length) {
                        try {
                            await ventilerDecaissementsCrees(created, grouped);
                        } catch (ventErr) {
                            toast.warn("Règlement créé, mais la ventilation par garantie a échoué. Vous pourrez la refaire via « Ventiler par garantie ».");
                        }
                    }
                    toast.success('Decaissement effectué !');
                    setDecaissementLoading(false);
                    modalControl.setOpen(false);
                    window.location.reload();
                    if (dossierCloture) {
                        handleClotureDossier();
                    }
                } catch (e) {
                    setDecaissementLoading(false);
                    toast.error(e?.response?.data?.message || `Une erreur s'est produite !`);
                }
                //      modalControl.handleSubmit(result);
            }

            }
        >
            {resteDisponible != null && (
                <Alert
                    type={depasseSap ? "error" : "info"}
                    showIcon
                    style={{ marginBottom: 12, borderRadius: 8 }}
                    message={<span>
                        Disponible pour un nouveau règlement : <b>{Number(resteDisponible).toLocaleString("fr-FR")} F</b>
                        {depasseSap && <> — la somme saisie ({montantDemande.toLocaleString("fr-FR")} F) dépasse ce plafond.</>}
                    </span>}
                />
            )}
            <Row gutter={[16, 16]}>

                <Col xs={24} md={12}>
                    <label
                        style={{
                            margin: "15px 0 5px 0",
                        }}
                    >
                        Nature du règlement *
                    </label>
                    <Select
                        mode="multiple"
                        name="natureReglement"
                        value={natureReglement}
                        placeholder="Sélectionner la nature du règlement"
                        style={{ width: "100%" }}
                        onChange={(value) => {
                            const selectedValues = Array.isArray(value) ? value : [];
                            setNatureReglement(selectedValues);
                            // Purge la ventilation des natures retirées (les montants se recalculent seuls).
                            setVentNature((prev) => {
                                const next = {};
                                selectedValues.forEach((n) => { if (prev[n]) next[n] = prev[n]; });
                                return next;
                            });
                            if (!selectedValues.includes("HONORAIRES")) {
                                setHonoraire(0);
                            }
                        }}
                        options={[
                            {
                                label: "Sinistre en principal",
                                value: "SINISTRE EN PRINCIPAL"
                            },
                            {
                                label: "Frais",
                                value: "ACCESSOIRES"
                            },
                            {
                                label: "Honoraires",
                                value: "HONORAIRES"
                            },
                            {
                                label: "Provision",
                                value: "PROVISION"
                            }
                        ]} />

                </Col>
                <Col xs={24} md={12}>
                    <label
                        style={{
                            margin: "15px 0 5px 0",
                        }}
                    >
                        Sélectionner le bénéficiaire *
                    </label>
                    <Select
                        name="beneficiaire"
                        value={selectedBeneficiaireId || undefined}
                        placeholder="Sélectionner le bénéficiaire"
                        style={{ width: "100%" }}
                        onChange={handleBeneficiaireChange}
                        options={beneficiaryOptionGroups}
                        allowClear
                        disabled={beneficiarySelectDisabled}
                        notFoundContent="Aucun bénéficiaire disponible"
                    />
                </Col>
                {selectedVictime ? (
                    <Col span={24}>
                        <div style={{ marginTop: 8, padding: 16, border: "1px solid #24216622", borderRadius: 12, background: "#24216608" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                                <SolutionOutlined style={{ color: "#242166" }} />
                                <Typography.Text strong style={{ color: "#242166" }}>
                                    À l'ordre de qui établir l'ordre de dépense / le chèque ?
                                </Typography.Text>
                                {selectedVictime?.decesSinistre
                                    ? <Tag color="red">Victime décédée</Tag>
                                    : selectedVictime?.blesseSinistre
                                        ? <Tag color="orange">Victime blessée</Tag>
                                        : null}
                            </div>

                            <Radio.Group
                                value={chequeMode}
                                onChange={(e) => setChequeMode(e.target.value)}
                                style={{ display: "flex", gap: 10, width: "100%", flexWrap: "wrap" }}
                            >
                                {[
                                    { key: "VICTIME", icon: <UserOutlined />, title: "La victime", sub: victimeName, disabled: false },
                                    { key: "REPRESENTANT", icon: <TeamOutlined />, title: "Un représentant", sub: representantCandidates.length ? "Représentant / ayant droit" : "Aucun — à créer sur la fiche victime", disabled: !representantCandidates.length },
                                ].map((o) => {
                                    const active = chequeMode === o.key;
                                    return (
                                        <label key={o.key} style={{
                                            flex: 1, minWidth: 200, cursor: o.disabled ? "not-allowed" : "pointer",
                                            border: `1.5px solid ${active ? "#242166" : "#e5e7eb"}`,
                                            background: "#fff", opacity: o.disabled ? 0.55 : 1,
                                            borderRadius: 10, padding: "10px 12px",
                                        }}>
                                            <Radio value={o.key} disabled={o.disabled} />
                                            <span style={{ marginLeft: 4, fontWeight: 600 }}>{o.icon} {o.title}</span>
                                            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>{o.sub}</div>
                                        </label>
                                    );
                                })}
                            </Radio.Group>

                            {chequeMode === "REPRESENTANT" && representantCandidates.length > 0 && (
                                <Select
                                    style={{ width: "100%", marginTop: 12 }}
                                    value={chequeRepresentant}
                                    onChange={setChequeRepresentant}
                                    placeholder="Choisir le représentant"
                                    options={representantCandidates.map((c) => ({
                                        value: c.value,
                                        label: <span>{c.value} <Tag style={{ marginLeft: 6 }} color={c.role === "Représentant" ? "geekblue" : "gold"}>{c.role}</Tag></span>,
                                    }))}
                                />
                            )}

                            {chequeMode === "REPRESENTANT" && !representantCandidates.length && (
                                <Alert style={{ marginTop: 12 }} type="warning" showIcon
                                    message="Aucun représentant ni ayant droit"
                                    description="Ajoutez-en un depuis la fiche de la victime, puis relancez le règlement." />
                            )}

                            <div style={{ marginTop: 12, padding: "8px 12px", background: "#fff", borderRadius: 8, border: "1px solid #eef0f4" }}>
                                <Typography.Text style={{ fontSize: 12 }}>
                                    Ordre de dépense / chèque à l'ordre de{" "}
                                    <Typography.Text strong>{chequeMode === "REPRESENTANT" ? (chequeRepresentant || "…") : victimeName}</Typography.Text>
                                    {chequeMode === "REPRESENTANT" && <> — pour le compte de <Typography.Text strong>{victimeName}</Typography.Text></>}.
                                </Typography.Text>
                            </div>
                        </div>
                    </Col>
                ) : null}


                <Col span={24}>
                    <label
                        style={{
                            margin: "15px 0 5px 0",
                        }}
                    >
                        Sélection le(s) préjudice(s)
                    </label>
                    <Select
                        name="sousEvaluations"
                        value={sousEvaluationsSelected}
                        placeholder="Sélectionner le(s) préjudice(s)"
                        style={{ width: "100%" }}
                        mode="tags"
                        onChange={handleSousEvChange}
                        options={[
                            {
                                label: "Préjudices matériels (dommages aux biens)",
                                value: "Préjudices matériels"
                            }, {
                                label: "Préjudices corporels (dommages physiques)",
                                value: "Préjudices corporels"
                            }, {
                                label: "Préjudices subis par les ayants droit du décédé",
                                value: "Préjudices subis par les ayants droit du décédé"
                            }, {
                                label: "Frais",
                                value: "Frais"
                            }, {
                                label: "Honoraires",
                                value: "Honoraires"
                            }
                        ]} />

                </Col>
                <Col span={24}>
                    <label
                        style={{
                            margin: "15px 0 5px 0",
                        }}
                    >
                        Motif de règlement *
                    </label>
                    <Spin spinning={motifsLoading}>
                        <Select
                            id="motifRevision"
                            name="motifRevision"
                            placeholder="Motif du règlement"
                            style={{ width: '100%', height: '50px' }}
                            dropdownRender={menu => (
                                <>
                                    {menu}
                                    <Divider style={{ margin: '8px 0' }} />
                                    <Space style={{ padding: '0 8px 4px' }}>
                                        <Input
                                            placeholder="Ajouter un motif"
                                            ref={inputRef}
                                            value={name}
                                            onChange={(e) => setName(e.target.value.toUpperCase())}
                                            onPressEnter={addItem}
                                        />
                                        <Button
                                            type="text"
                                            icon={<PlusOutlined />}
                                            onClick={addItem}
                                        >
                                            Ajouter un motif
                                        </Button>
                                    </Space>
                                </>
                            )}
                            options={dedupMotifOptions(motifs)}
                            onChange={onMotifSelect}
                            value={motifReglement}
                        />
                        <TextArea
                            rows={5}
                            value={textValue}
                            onChange={(e) => setTextValue(e.target.value)}
                            placeholder="Modifier le motif ici..."
                            style={{ marginTop: 10 }}
                        />
                    </Spin>
                </Col>
                {/* ── Panneau CONSEIL JURIDIQUE (remplace la ventilation par garantie) ── */}
                {isConseilSelected && (
                    <Col span={24}>
                        <div style={{ marginTop: 12, padding: "14px 16px", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 10 }}>
                            <div style={{ fontWeight: 600, color: "#242166", marginBottom: 6 }}>
                                Règlement du conseil juridique — {conseilJuridique?.nom}
                            </div>
                            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
                                Sélectionnez Honoraires et/ou Frais (les deux ensemble = un seul règlement groupé).
                                Les honoraires portent TVA + retenue ; les frais sont réglés à valeur faciale. Le plafond
                                de déplacement, les juridictions exclues et l'unicité des frais de gestion / PV de constat
                                sont contrôlés à la validation.
                            </div>
                            {natureReglement?.includes("HONORAIRES") && (
                                <Row gutter={12} style={{ marginBottom: 8 }}>
                                    <Col xs={24} md={8}>
                                        <label style={{ display: "block", margin: "4px 0" }}>Honoraires (HT) *</label>
                                        <AmountInputNumber style={{ width: "100%" }} placeholder="Montant HT"
                                            value={montantHonorairesConseil} onChange={(v) => setMontantHonorairesConseil(v || 0)} />
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <label style={{ display: "block", margin: "4px 0" }}>TVA (%)</label>
                                        <AmountInputNumber style={{ width: "100%" }} placeholder="TVA"
                                            value={tauxTvaConseil} onChange={(v) => setTauxTvaConseil(v || 0)} />
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <label style={{ display: "block", margin: "4px 0" }}>Net honoraires (indicatif)</label>
                                        <div style={{ padding: "6px 0", fontWeight: 600, color: "#242166" }}>
                                            {formatAmount(
                                                (parseFloat(montantHonorairesConseil) || 0)
                                                * (1 + (parseFloat(tauxTvaConseil) || 0) / 100 - (parseFloat(honoraire) || CONSEIL_RETENUE_DEFAUT) / 100),
                                                { emptyValue: "0" }
                                            )} F
                                        </div>
                                    </Col>
                                </Row>
                            )}
                            {natureReglement?.includes("ACCESSOIRES") && (
                                <>
                                    <div style={{ fontSize: 12, color: "#6b7280", margin: "4px 0 6px" }}>
                                        Frais (valeur faciale) — renseignez un montant pour chaque poste concerné :
                                    </div>
                                    <Row gutter={12}>
                                        {POSTES_FRAIS_CONSEIL.map((p) => (
                                            <Col xs={24} md={12} key={p.value} style={{ marginBottom: 8 }}>
                                                <label style={{ display: "block", margin: "4px 0" }}>{p.label}</label>
                                                <AmountInputNumber style={{ width: "100%" }} placeholder="0"
                                                    value={fraisConseil[p.value]}
                                                    onChange={(v) => setFraisConseil((prev) => ({ ...prev, [p.value]: v || 0 }))} />
                                            </Col>
                                        ))}
                                    </Row>
                                </>
                            )}
                            {!(natureReglement?.includes("HONORAIRES") || natureReglement?.includes("ACCESSOIRES")) && (
                                <Alert type="warning" showIcon
                                    message="Sélectionnez Honoraires et/ou Frais dans la nature du règlement ci-dessus." />
                            )}
                            {/* Ventilation par garantie (facultative) — répartie au prorata sur chaque
                                poste créé (honoraires + frais). La somme par décaissement = son montant. */}
                            {sapGarantiesVent.length > 0
                                && (natureReglement?.includes("HONORAIRES") || natureReglement?.includes("ACCESSOIRES")) && (
                                <div style={{ marginTop: 14 }}>
                                    <div style={{ fontWeight: 600, color: "#242166", marginBottom: 6 }}>
                                        Ventilation par garantie (facultative)
                                    </div>
                                    <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>
                                        Saisissez une répartition indicative par garantie ; elle sera appliquée AU PRORATA
                                        sur chaque poste réglé (honoraires + frais) pour le suivi du SAP par garantie.
                                    </div>
                                    <Table rowKey={(r) => gkeyVent(r)} size="small" pagination={false} dataSource={sapGarantiesVent}
                                        columns={[
                                            { title: "Garantie", dataIndex: "garantieNom", render: (v, r) => libelleGarantie(v) || r.garantieCodeId || "—" },
                                            { title: "SAP", dataIndex: "sap", align: "right", render: (v) => <b style={{ color: (Number(v) || 0) > 0 ? "#b91c1c" : "#16a34a" }}>{Number(v || 0).toLocaleString("fr-FR")} F</b> },
                                            {
                                                title: "Poids / à imputer", key: "m", align: "right", width: 180,
                                                render: (_, g) => (
                                                    <AmountInputNumber style={{ width: "100%" }} value={ventConseil[gkeyVent(g)] || 0}
                                                        onChange={(val) => setVentConseil((m) => ({ ...m, [gkeyVent(g)]: val || 0 }))} />
                                                ),
                                            },
                                        ]} />
                                </div>
                            )}
                        </div>
                    </Col>
                )}
                {/* Ventilation par nature × garantie : une table par nature. Le montant de la nature
                    (et le total) découle des valeurs saisies par garantie. */}
                {!isConseilSelected && natureReglement?.map((reglement, index) => (
                    <Col span={24} key={`${reglement}-${index}`}>
                        <div style={{ marginTop: 12, padding: "12px 14px", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 10 }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                                <div style={{ fontWeight: 600, color: "#242166" }}>
                                    Ventilation par garantie — {labelNature(reglement)} *
                                    {tauxResponsabilite < 100 && NATURES_PONDEREES_RESPONSABILITE.includes(reglement) && (
                                        <div style={{ fontWeight: 400, fontSize: 12, color: "#b45309", marginTop: 2 }}>
                                            Taux de responsabilité {tauxResponsabilite}% : la répartition prorata SAP est pondérée (montant × {tauxResponsabilite}%).
                                        </div>
                                    )}
                                </div>
                                {sapGarantiesVent.length > 0 && (
                                    <Space wrap>
                                        <AmountInputNumber size="small" style={{ width: 150 }} placeholder="Montant à répartir"
                                            value={ventCible[reglement] || 0}
                                            onChange={(v) => setVentCible((m) => ({ ...m, [reglement]: v || 0 }))} />
                                        <Button size="small" type="primary" ghost
                                            onClick={() => repartirNatureProrataSap(reglement, ventCible[reglement])}>
                                            {tauxResponsabilite < 100 && NATURES_PONDEREES_RESPONSABILITE.includes(reglement)
                                                ? `Répartir au prorata (× ${tauxResponsabilite}% resp.)`
                                                : "Répartir au prorata du SAP"}
                                        </Button>
                                        <Button size="small" onClick={() => setVentNature((p) => ({ ...p, [reglement]: {} }))}>Effacer</Button>
                                    </Space>
                                )}
                            </div>
                            {sapGarantiesVent.length > 0 ? (
                                <Table rowKey={(r) => gkeyVent(r)} size="small" pagination={false} dataSource={sapGarantiesVent}
                                    columns={[
                                        { title: "Garantie", dataIndex: "garantieNom", render: (v, r) => libelleGarantie(v) || r.garantieCodeId || "—" },
                                        { title: "Provision", dataIndex: "provision", align: "right", render: (v) => `${Number(v || 0).toLocaleString("fr-FR")} F` },
                                        { title: "SAP", dataIndex: "sap", align: "right", render: (v) => <b style={{ color: (Number(v) || 0) > 0 ? "#b91c1c" : "#16a34a" }}>{Number(v || 0).toLocaleString("fr-FR")} F</b> },
                                        {
                                            title: "À imputer", key: "m", align: "right", width: 180,
                                            render: (_, g) => (
                                                <AmountInputNumber style={{ width: "100%" }} value={ventNature[reglement]?.[gkeyVent(g)] || 0}
                                                    onChange={(val) => setVentCell(reglement, gkeyVent(g), val)} />
                                            ),
                                        },
                                    ]}
                                    summary={() => (
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0} colSpan={3}><b>Total {labelNature(reglement)}</b></Table.Summary.Cell>
                                            <Table.Summary.Cell index={1} align="right"><b>{Number(montantParNature[reglement] || 0).toLocaleString("fr-FR")} F</b></Table.Summary.Cell>
                                        </Table.Summary.Row>
                                    )} />
                            ) : (
                                <>
                                    <label style={{ display: "block", margin: "4px 0" }}>Montant — {labelNature(reglement)}</label>
                                    <AmountInputNumber style={{ width: "100%" }} placeholder="Saisir le montant"
                                        value={ventNature[reglement]?.[VENT_NONE] || 0}
                                        onChange={(val) => setVentCell(reglement, VENT_NONE, val)} />
                                </>
                            )}
                        </div>
                    </Col>
                ))}
                {natureReglement?.length > 1 && (
                    <Col xs={24}>
                        <div style={{
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            gap: 12, padding: "12px 14px", marginTop: 8,
                            background: "#f6f7fb", border: "1px solid #e5e7eb", borderRadius: 10,
                        }}>
                            <div>
                                <div style={{ fontWeight: 600, color: "#242166" }}>Règlement groupé</div>
                                <div style={{ fontSize: 12, color: "#6b7280" }}>
                                    {regrouper
                                        ? "Un seul ordre de dépense, une seule fiche, une seule quittance et un seul chèque pour toutes les natures."
                                        : "Documents et paiements séparés (un par nature)."}
                                </div>
                            </div>
                            <Switch
                                checked={regrouper}
                                onChange={setRegrouper}
                                checkedChildren="Groupé"
                                unCheckedChildren="Séparé"
                            />
                        </div>
                    </Col>
                )}
                {natureReglement?.includes("HONORAIRES") && (
                    <Col xs={24} md={natureReglement?.length === 1 ? 12 : 24}>
                        <label
                            style={{
                                margin: "15px 0 5px 0",
                            }}
                        >
                            Taux retenue *
                        </label>
                        <Select
                            name="tauxRetenue"
                            value={honoraire}
                            placeholder="Sélectionner le taux de retenue"
                            style={{ width: "100%" }}
                            onChange={setHonoraire}
                            options={[
                                {
                                    label: "3%",
                                    value: 3
                                },
                                {
                                    label: "5%",
                                    value: 5
                                },
                                {
                                    label: "20%",
                                    value: 20
                                },
                            ]} />
                    </Col>
                )}
                {!isConseilSelected && honoraire ? (
                    <Col xs={24} md={12}>
                        <label
                            style={{
                                margin: "15px 0 5px 0",
                            }}
                        >
                            Montant retenue :{" "}
                            {formatAmount(
                                (parseFloat(honoraire) / 100) * parseFloat(montantNature?.find((nature) => nature.nature === "HONORAIRES")?.montant || 0),
                                { emptyValue: "0" }
                            )}
                        </label>
                    </Col>
                ) : null}
                {!isConseilSelected && (
                    <Col xs={24} md={(natureReglement?.includes("HONORAIRES") && natureReglement?.length === 1) ? 12 : 24}>
                        <label
                            style={{
                                margin: "15px 0 5px 0",
                            }}
                        >
                            Montant total
                        </label>
                        <AmountInputNumber
                            name="montantReglement"
                            value={montantReglement}
                            style={{ width: '100%' }}
                            disabled
                        />
                    </Col>
                )}
                {/* La formule de conclusion n'est plus saisie ici : elle est choisie (facultative)
                    au moment de l'édition de la quittance, dans AssignCheque. */}
                {/* La ventilation par garantie est désormais faite par nature ci-dessus (matrice). */}

                <Col span={24}>
                    <Switch checked={dossierCloture} checkedChildren={'Dossier clôturé'}
                        unCheckedChildren={'Dossier en instance'} disabled style={{
                            marginTop: '20px'
                        }} />
                </Col>
            </Row>
        </Modal >
    </>);
};

export default ReglementModal;
